import { Training, TrainingStats } from '../../services/firebase/training.service';
import { TrainingSessionData, TrainingType, TrainingPartner } from '../../components/TrainingComponents/TrainingSession';
import { TrainingRecord } from '../../components/TrainingComponents/TrainingLog';
import { formatLocalDateKey, getDateWithTimezone } from '../date-time';

/**
 * Maps the Firebase training type to the component's TrainingType
 * Firebase stores: 'training', 'seminar', 'camp', 'teaching', 'competition'
 */
export const mapTrainingType = (type: string): TrainingType => {
  switch (type) {
    case 'training':
      return 'training';
    case 'seminar':
      return 'seminar';
    case 'camp':
      return 'camp';
    case 'teaching':
      return 'teaching';
    case 'competition':
      return 'competition';
    default:
      // Default to 'training' for any unknown type
      return 'training';
  }
};

/**
 * Formats training type for display with proper labels
 */
export const formatTrainingType = (type: string | undefined, t: (key: string) => string): string => {
  if (!type) {
    return t('training.types.default') || 'Training Session';
  }
  
  const typeMap: Record<string, string> = {
    'gi': 'training.types.gi',
    'training': 'training.types.gi',
    'nogi': 'training.types.nogi',
    'openmat': 'training.types.open_mat',
    'open_mat': 'training.types.open_mat',
    'competition': 'training.types.competition',
    'seminar': 'training.types.seminar',
    'private': 'training.types.private',
    'drilling': 'training.types.drilling',
    'teaching': 'training.types.teaching',
    'other': 'training.types.other'
  };
  
  const key = typeMap[type.toLowerCase()];
  if (key) {
    return t(key) || 'Training Session';
  }
  
  return t('training.types.default') || 'Training Session';
};

/**
 * Converts a Firebase training object to the TrainingSessionData format used by the UI components
 */
export const adaptTrainingToSessionData = (training: Training): TrainingSessionData => {
  // Extract first photo URL if available
  // Original app stores photos as object with numeric keys: { "0": {url, aspectRatio}, "1": {...} }
  let photoUrl: string | undefined;

  // Priority 1: Check photos object (original app format)
  if (training.photos) {
    const photoKeys = Object.keys(training.photos);
    if (photoKeys.length > 0) {
      const firstPhoto = training.photos[photoKeys[0]];
      photoUrl = firstPhoto?.url;
    }
  }

  // Priority 2: Fallback to legacy trainingPhoto field (single photo)
  if (!photoUrl && training.trainingPhoto && training.trainingPhoto.url) {
    photoUrl = training.trainingPhoto.url;
  }
  
  // Extract first video URL if available
  let videoUrl: string | undefined;
  if (training.videosYT && Object.keys(training.videosYT).length > 0) { // Use correct field name
    const youtubeId = Object.values(training.videosYT)[0]; // Original format: Record<string, string>
    videoUrl = `https://www.youtube.com/watch?v=${youtubeId}`;
  }
  
  
  // Map training partners (if available)
  const partners: TrainingPartner[] = [];
  // In the original app, training partners might be stored differently
  // Here we assume they could be in a 'partners' field or a custom structure
  if (training.taggedFriends) {
    Object.entries(training.taggedFriends).forEach(([id, data]: [string, any]) => {
      partners.push({
        id,
        name: data.username || data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unknown',
        avatarUrl: data.profilePhotoURL || data.photoURL,
        beltRank: data.beltDesignation || data.belt // if available
      });
    });
  }
  
  // Calculate rounds if available
  let rounds = undefined;
  if (training.rounds) {
    rounds = Object.keys(training.rounds).length;
  }
  
  return {
    id: training.id,
    date: new Date(training.date),
    type: mapTrainingType(training.type),
    totalTrainedTime: training.totalTrainedTime,
    rollTime: training.rollTime,
    gi: training.gi,
    trainedStyle: training.trainedStyle,
    location: training.location,
    instructor: training.instructor, // Original format is string uid
    note: training.note, // Use correct field name
    trainingPartners: partners,
    flows: training.flows, // Include flows
    rounds,
    photoUrl,
    videoUrl
  };
};

/**
 * Converts Firebase training records into the format needed for the TrainingLog component
 */
export const adaptTrainingsToLogRecords = (
  trainings: Training[],
  startDate: Date,
  endDate: Date
): TrainingRecord[] => {
  // Create a map of date to training record
  const recordsMap: Record<string, TrainingRecord> = {};
  
  // Initialize records for all dates in range
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const dateKey = formatLocalDateKey(currentDate);
    recordsMap[dateKey] = {
      date: dateKey,
      sessionCount: 0,
      duration: 0,
      types: {}
    };
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Populate with actual training data.
  // TZ (QH4): bucket by the training's ORIGINAL-timezone day, matching the
  // stats-aggregation path (stats-data-adapter). Keying off the raw
  // `new Date(training.date)` here bucketed by the VIEWER's day, so a 9pm-NYC
  // training viewed from another TZ landed on a different day in the calendar
  // than in the monthly/stats breakdown.
  trainings.forEach(training => {
    const date = getDateWithTimezone(training.date, training.timezoneOffset);
    const dateKey = formatLocalDateKey(date);

    // Skip if outside our range
    if (!recordsMap[dateKey]) return;
    
    // Update session count
    recordsMap[dateKey].sessionCount += 1;
    
    // Update duration
    recordsMap[dateKey].duration += training.totalTrainedTime;
    
    // Update types
    const type = mapTrainingType(training.type);
    recordsMap[dateKey].types[type] = (recordsMap[dateKey].types[type] || 0) + 1;
  });
  
  // Convert map to array
  return Object.values(recordsMap);
};

/**
 * Extracts summary statistics from training records for display
 */
export const extractTrainingSummaryStats = (trainings: Training[]): {
  totalSessions: number;
  totalHours: number;
  totalDays: number;
  averageSessionsPerWeek: number;
} => {
  if (trainings.length === 0) {
    return {
      totalSessions: 0,
      totalHours: 0,
      totalDays: 0,
      averageSessionsPerWeek: 0
    };
  }
  
  // Count unique training days
  const trainingDays = new Set<string>();
  let totalMinutes = 0;
  
  trainings.forEach(training => {
    // TZ (QH4): count unique days by original-TZ day, not viewer day.
    const dateKey = formatLocalDateKey(getDateWithTimezone(training.date, training.timezoneOffset));
    trainingDays.add(dateKey);
    totalMinutes += training.totalTrainedTime;
  });
  
  // Calculate date range
  const dates = trainings.map(t => new Date(t.date).getTime());
  const firstDate = new Date(Math.min(...dates));
  const lastDate = new Date(Math.max(...dates));
  const daysDiff = Math.ceil((lastDate.getTime() - firstDate.getTime()) / (24 * 60 * 60 * 1000));
  const weeksDiff = Math.max(1, Math.ceil(daysDiff / 7));
  
  return {
    totalSessions: trainings.length,
    totalHours: Math.round(totalMinutes / 60),
    totalDays: trainingDays.size,
    averageSessionsPerWeek: +(trainings.length / weeksDiff).toFixed(1)
  };
};

/**
 * Adapts Firebase training stats to UI component format
 */
export const adaptTrainingStats = (stats: TrainingStats) => {
  return {
    totalSessions: stats.totalSessions,
    totalHours: Math.round(stats.totalTrainingTime / 60),
    totalDays: stats.streak.longest, // This is an approximation
    sessionsPerWeek: stats.weeklyAverage,
    sessionsLastWeek: stats.sessionsLastWeek,
    sessionsLastMonth: stats.sessionsLastMonth,
    sessionsThisYear: stats.sessionsThisYear,
    popularTypes: Object.entries(stats.byType)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({ type, count })),
    currentStreak: stats.streak.current,
    longestStreak: stats.streak.longest
  };
};

/**
 * Groups trainings by date for the TrainingsList component
 */
export const groupTrainingsByDate = (
  trainings: Training[]
): Record<string, TrainingSessionData[]> => {
  const grouped: Record<string, TrainingSessionData[]> = {};
  
  trainings.forEach(training => {
    // TZ (QH4): group by original-TZ day, matching the calendar + stats paths.
    const date = getDateWithTimezone(training.date, training.timezoneOffset);
    const dateKey = formatLocalDateKey(date);

    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }

    grouped[dateKey].push(adaptTrainingToSessionData(training));
  });
  
  return grouped;
};

/**
 * Filters trainings by type
 */
export const filterTrainingsByType = (
  trainings: Training[],
  type: TrainingType | 'all'
): Training[] => {
  if (type === 'all') {
    return trainings;
  }
  
  return trainings.filter(training => {
    return mapTrainingType(training.type) === type;
  });
};